import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { Button } from 'app/components/ui/button';
import { Checkbox } from 'app/components/ui/checkbox';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'app/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'app/components/ui/form';
import { ExternalLink, Share2, Save } from 'lucide-react';
import { useSocialLinksSlice } from 'app/pages/SocialLinks/slice';
import { toast } from 'app/components/ui/use-toast';

// Use the existing SocialLink type but override the fields that differ in the API response
interface SocialLinkData {
  id: number;
  platform_name: string;
  url: string;
  is_floating_enabled: number;
  status: number;
  created_at: string;
  updated_at: string;
}

interface FormData {
  [key: string]: string | boolean | undefined;
}

export default function SocialLinksSettings() {
  const { useGetSocialLinksQuery, useUpdateSocialLinkMutation } =
    useSocialLinksSlice();
  const [getSocialLinks, { data: socialLinksResponse, isLoading, isError }] =
    useGetSocialLinksQuery();
  const [updateSocialLinks, { isLoading: isUpdating, data, error }] =
    useUpdateSocialLinkMutation();

  const form = useForm<FormData>({
    defaultValues: {},
  });

  useEffect(() => {
    getSocialLinks();
  }, []);

  // Initialize form with existing data when loaded
  useEffect(() => {
    if (socialLinksResponse?.response?.data) {
      const socialLinks = socialLinksResponse.response
        .data as unknown as SocialLinkData[];
      const defaultValues: FormData = {};

      socialLinks.forEach(link => {
        const platformKey = link.platform_name.toLowerCase();
        defaultValues[`social_${platformKey}`] = link.url || '';
        defaultValues[`${platformKey}_floating_enabled`] =
          link.is_floating_enabled === 1;
      });

      form.reset(defaultValues);
    }
  }, [socialLinksResponse, form]);

  const onSubmit = (data: FormData) => {
    const updatedSocialLinks = socialLinksResponse?.response?.data.map(
      (link: any) => {
        const platformKey = link.platform_name.toLowerCase();
        const urlKey = `social_${platformKey}`;
        const floatingKey = `${platformKey}_floating_enabled`;
        const url = data[urlKey] as string;
        const isFloating = data[floatingKey] as boolean;

        return {
          id: link.id,
          platform_name: link.platform_name,
          url,
          is_floating_enabled: isFloating ? 1 : 0,
          status: link.status,
        };
      },
    );
    console.log(updatedSocialLinks);

    updateSocialLinks({
      socialLinks: updatedSocialLinks,
    });
  };

  useEffect(() => {
    if (data) {
      toast({
        title: 'Success',
        description: 'Social media settings updated successfully.',
        variant: 'success',
      });
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      toast({
        title: 'Error',
        description:
          'Failed to update social media settings. Please try again.',
        variant: 'destructive',
      });
    }
  }, [error]);

  // Memoized social link field component
  const SocialLinkField = useCallback(
    ({ link }: { link: SocialLinkData }) => {
      const platformKey = link.platform_name.toLowerCase();
      const urlFieldName = `social_${platformKey}`;
      const floatingFieldName = `${platformKey}_floating_enabled`;

      // Generate placeholder based on platform name
      const getPlaceholder = (platformName: string) => {
        const platform = platformName.toLowerCase();
        switch (platform) {
          case 'facebook':
            return 'https://facebook.com/your-page';
          case 'twitter':
            return 'https://twitter.com/your-handle';
          case 'instagram':
            return 'https://instagram.com/your-handle';
          case 'linkedin':
            return 'https://linkedin.com/in/your-profile';
          case 'youtube':
            return 'https://youtube.com/@your-channel';
          case 'telegram':
            return 'https://t.me/yourchannel';
          case 'whatsapp':
            return 'https://wa.me/yournumber';
          default:
            return `https://${platform}.com/your-profile`;
        }
      };

      return (
        <div className="space-y-2">
          <FormField
            control={form.control}
            name={urlFieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{link.platform_name}</FormLabel>
                <FormControl>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Input
                        {...field}
                        value={field.value as string}
                        placeholder={getPlaceholder(link.platform_name)}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        if (field.value) {
                          window.open(
                            field.value as string,
                            '_blank',
                            'noopener,noreferrer',
                          );
                        }
                      }}
                      disabled={!field.value}
                      title="Open link in new tab"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={floatingFieldName}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={field.value as boolean}
                      onCheckedChange={field.onChange}
                    />
                    <Label className="text-sm text-gray-600">
                      Show as floating button
                    </Label>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      );
    },
    [form],
  );

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Social Media Links
          </CardTitle>
          <CardDescription>Loading social media settings...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Social Media Links
          </CardTitle>
          <CardDescription>
            Error loading social media settings.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-red-500 text-center py-4">
            Failed to load social media settings. Please try again.
          </div>
        </CardContent>
      </Card>
    );
  }

  const socialLinks =
    (socialLinksResponse?.response?.data as unknown as SocialLinkData[]) || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Share2 className="w-5 h-5" />
          Social Media Links
        </CardTitle>
        <CardDescription>
          Configure your social media profiles and floating button settings.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {socialLinks.map(link => (
                <SocialLinkField key={link.id} link={link} />
              ))}
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={isUpdating}
                className="flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {isUpdating ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
